import React, { useState, useEffect, useCallback } from 'react';
import { assetService } from '../services/api';
import { Asset } from '../types';
import { toast } from 'react-hot-toast';

export const InventoryPage: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'consumable' | 'equipment' | 'vehicle'>('all');

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await assetService.getInventory(filter === 'all' ? undefined : filter)).data;
      setAssets(response);
    } catch (error) {
      toast.error('Failed to fetch inventory parts');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleStatusUpdate = async (assetId: string, status: string) => {
    try {
      await assetService.updateAssetStatus(assetId, status, '');
      setAssets(assets.map(a => (a.id || a._id) === assetId ? { ...a, status: status as any } : a));
      toast.success('Part stock updated');
    } catch (error) {
      toast.error('Failed to update part status');
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto font-sans min-w-0 max-w-full overflow-hidden">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-2.5 sm:gap-3">
          <span>Parts &amp; Stock Inventory</span>
          <button onClick={fetchAssets} className="p-1.5 sm:p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full transition-colors border border-slate-700" title="Refresh Data">
            <svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </h1>
        <p className="text-slate-400 text-xs sm:text-sm">Manage spare parts and consumables for verified repairs.</p>
      </div>
      
      <div className="mb-6 flex flex-wrap gap-2">
        {['all', 'consumable', 'equipment', 'vehicle'].map((type) => (
          <button
            key={type}
            onClick={() => setFilter(type as any)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition ${
              filter === type 
                ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg shadow-orange-500/20' 
                : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950 border-b border-slate-800 text-xs font-bold text-slate-400 uppercase tracking-wider">
                <th className="px-6 py-4">Part ID</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Stock Qty</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading inventory...</td></tr>
              ) : assets.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No parts found matching this filter.</td></tr>
              ) : (
                assets.map((asset) => (
                  <tr key={asset.id || asset._id} className="hover:bg-slate-800/50 transition">
                    <td className="px-6 py-4 font-mono text-slate-300 text-sm">{asset.assetId}</td>
                    <td className="px-6 py-4 text-white font-medium">{asset.assetName}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{asset.assetType}</td>
                    <td className="px-6 py-4 font-bold text-white">{asset.quantity}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${
                        asset.status === 'available' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                        asset.status === 'in_use' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                        'bg-rose-500/10 text-rose-400 border-rose-500/20'
                      }`}>
                        {asset.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {asset.status === 'available' && (
                        <button
                          onClick={() => handleStatusUpdate((asset.id || asset._id) as string, 'maintenance')}
                          className="bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-500 px-3 py-1.5 rounded-lg text-xs font-bold transition"
                        >
                          Mark Depleted
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
